import { victoryAnnouncement } from "../../marqueeScheduler";

const syncVictoryAnnouncement = state => {
  // 结束记录保留给复盘，但reset后的权威gs.gameId已清空。
  // 等当前桌面范围确认后才能恢复公告，不改服务器历史记录。
  const notice = state.marqueeGameScopeId
    ? victoryAnnouncement(state.voiceState.game, state.sessionId) : null;
  if (notice?.key !== state.marqueeAnnouncement?.key)
    state.marqueeAnnouncement = notice;
};

/**
 * Handle a vote request.
 * If the vote is from a seat that is already locked, ignore it.
 * @param state session state
 * @param index seat of the player in the circle
 * @param vote true or false
 */
const handleVote = (state, [index, vote]) => {
  if (!state.nomination) return;
  state.votes = [...state.votes];
  state.votes[index] = vote === undefined ? !state.votes[index] : vote;
};

const state = () => ({
  sessionId: "",
  // 创建房间时声明的游玩人数。它只用于把“建房人数”告诉服务端做范围校验
  // （Join 房间与旁观者始终为 0），不参与任何本地房间状态推断。
  requestedSeats: 0,
  offlineGrimoire: false,
  // Used only while establishing the WebSocket connection to a locked room.
  // It is never included in shared game state or written into the room list.
  roomPassword: "",
  isSpectator: false,
  isOwner: false,
  isReconnecting: false,
  playerCount: 0,
  ping: 0,
  playerId: "",
  storytellerProfile: { id: "", name: "空", avatar: "" },
  claimedSeat: -1,
  // This value is written only after the realtime server confirms the full
  // seat layout. It must not be inferred from a locally persisted avatar,
  // otherwise a stale visual seat turns the first “坐下” click into “起立”.
  confirmedSeat: -1,
  nomination: false,
  voteInstance: 0,
  voteRestartContext: null,
  votes: [],
  lockedVote: 0,
  votingSpeed: 3000,
  isVoteInProgress: false,
  voteHistory: [],
  // 当前正在查看的那一局的 gameId。投票记录按它过滤，所以新一局不会与
  // 旧局混在一起，旧记录本身仍然留在 voteHistory 里（供以后复盘使用）。
  voteHistoryGameId: "",
  dayNumber: 1,
  dayHistory: [],
  pendingDayResolution: null,
  markedPlayer: -1,
  isVoteHistoryAllowed: true,
  // 匿名投票（说书人手动主持工具，服务端权威下发）。默认关闭；它不是
  // 角色能力判定，也不自动绑定任何角色 ID。
  anonymousVote: false,
  // 匿名模式下“普通玩家允许看到的死人票公开基线”：由服务端在每个白天
  // 开始时（或当天开启匿名时）抓取，玩家在金蛋显示上优先读它，因此刷新 /
  // 重连不会提前泄露，也不会被旧快照回档。
  publicVoteless: null,
  isRolesDistributed: false,
  isOwnRoleHidden: false,
  // Compatibility choices imported from the newer project. They are kept in
  // room state so the storyteller's grimoire remains the single authority.
  bootlegger: "",
  isUseOldOrder: { pithag: false, professor: false },
  isUseOldRole: {
    balloonist: false,
    acrobat: false,
    lilmonsta: false,
    alchemist: false,
    lycanthrope: false
  },
  privateRoleNotes: {},
  bluffEligibility: null,
  bluffDeliveryStatus: { state: '', message: '' },
  bluffDraftStatus: { state: '', message: '' },
  privateRoleTypes: {},
  // A camp-only delivery deliberately contains no character. Keep its colour
  // private as well so the seat can show the same alignment ring immediately.
  privateRoleAlignments: {},
  // The player's own delivered identity is deliberately local-only. Public
  // seat-layout packets must never be able to erase it or reveal it.
  privateIdentities: {},
  // 仅说书人页面持有：服务端确认“完整身份已成功接受”后的座位快照。
  // 用于魔典红/绿同步点，不进入公开 gs，也不提供给玩家或旁观者。
  storytellerDeliveredIdentities: {},
  storytellerAuthoritativeIdentities: {},
  // 说书人页面的「发送中」簿记：index → { operationId, roleId, alignment }。
  // 只表示「这笔发送已经交给服务器、还没收到 ACK」。它与上面已确认的
  // storytellerDeliveredIdentities 严格区分：没有服务器确认，界面绝不
  // 允许显示成「已保存」。异步回执按 operationId 精确匹配，迟到的旧回执
  // 不会清掉或确认更新的那一笔。
  storytellerPendingIdentities: {},
  chatMessages: [],
  chatPolicy: { publicChatMaxLength: 1000, publicMarqueeMaxLength: 50, publicMarqueeEllipsis: true },
  chatSendError: "",
  // Private-chat motion is intentionally a separate, content-free room event.
  // Everybody may see that a note was sent, but only participants receive text.
  chatFlights: [],
  // 一次性玩家互动动画；没有历史恢复、持久化或重连补发。
  interactions: [],
  publicChatNotice: null,
  // 仅由实时chat广播追加。展示组件领取后删除，重挂载不重读历史通知。
  publicMarqueeEvents: [],
  publicMarqueeSeenIds: [],
  marqueeAnnouncement: null,
  marqueeGameScopeId: null,
  miniGameChatNotices: [],
  miniGames: null,
  miniGameNotice: "",
  // 服务端每次开始/结束一局都会推进它；展示层据此立即清掉仍在飞行的
  // 旧弹幕，而不是依赖动画自己跑完。
  chatEpoch: 0,
  chatGroups: [],
  // Private-chat tabs are a local convenience only. Messages themselves are
  // still routed and audited by the server; opening a tab leaks nothing.
  chatContacts: [],
  chatTarget: "public",
  // 房间级“白天文字私聊范围”。服务端是唯一权威：进入房间、刷新、重连时
  // 都会收到服务端的 chatMode 包再覆盖这里；它不参与任何一局的生命周期。
  chatMode: "allow_all",
  // 未读聊天（只用于界面红点）：保存“当前用户有权限收到、但还没看过”
  // 的消息 id。连接/刷新时服务端补发的历史消息由 socket 层排除，不会
  // 在刷新后把所有聊天重新变成未读。
  chatUnreadIds: [],
  chatReadIds: [],
  quickWakeTasks: [],
  // 「引用角色」的权威可选项：由服务端按当前房间剧本下发
  // （quickWakeSnapshot.roleReference），客户端不自己读全角色库。
  quickWakeRoleReference: [],
  quickWakeGlobalPresets: [],
  quickWakePersonalPresets: [],
  quickWakeEditor: { open: false, seatIndexes: [], taskId: "" },
  quickWakeLastResult: null,
  canViewGrimoire: false,
  receivedGrimoire: null,
  grimoireNotice: null,
  isPostGameReview: false,
  // 房间被解散/超时销毁/代际失效后的明确失效状态：只用来显示提示与
  // “返回主页”，不参与任何房间逻辑（房间本身已经被服务端删除）。
  roomClosedNotice: null,
  // 上一局的结果（胜利阵营）只用于复盘提示；是否“有一局正在进行中”
  // 永远以服务端 voiceState.game 为准，本地不做推断。
  gameResult: null,
  // Voice is deliberately separate from text chat. The server is the sole
  // authority for this phase, room membership and microphone permissions.
  voiceState: {
    phase: "night",
    dayIndex: 1,
    game: { id: "", number: 0, status: "idle", allRecall: false, startedAt: "", endedAt: "", winner: null },
    rooms: [],
    chairEnabled: false,
    chairSpeakerIds: [],
    mutedIds: [],
    allMuted: false
  },
  voiceToken: null,
  dayPrivateListen: { available: false, enabled: false, selectedSlot: 1, maxSlot: 10, revision: 0, status: "disabled", credentials: null },
  dayPrivateListenVolume: 100,
  publicVoiceToken: null,
  voiceSpeaking: { roomId: "", speakerIds: [] },
  discussionTimer: {
    durationSeconds: 0,
    remainingSeconds: 0,
    running: false,
    startedAt: null
  }
});

const getters = {
  // 「白天提名」是唯一允许出现提名与唱票界面的阶段。所有投票相关的
  // 入口统一读这个 getter，避免各处各写一遍判断而互相走样。
  isNominationPhase: state => state.voiceState.phase === "nomination",
  phaseDayIndex: state => Math.max(1, Math.floor(Number(state.voiceState.dayIndex)) || 1),
  // 界面只显示当前这一局的提名/投票记录；旧局记录继续留在 voteHistory 里。
  currentVoteHistory: state =>
    state.voteHistory.filter(vote => (vote.gameId || "") === (state.voteHistoryGameId || "")),
  // 被隐藏起来的旧局记录条数（仅用于提示，不影响存档）。
  archivedVoteHistoryCount: (state, getters) =>
    state.voteHistory.length - getters.currentVoteHistory.length
};

const actions = {};

// mutations helper functions
const set = key => (state, val) => {
  state[key] = val;
};

const mutations = {
  setPlayerId: set("playerId"),
  setRoomPassword: set("roomPassword"),
  setOfflineGrimoire(state, value) { state.offlineGrimoire = value === true; },
  setStorytellerAuthority(state, identities = {}) { state.storytellerAuthoritativeIdentities = { ...identities }; },
  setRequestedSeats(state, seats) {
    const value = Number(seats);
    state.requestedSeats = Number.isInteger(value) && value > 0 ? value : 0;
  },
  setStorytellerProfile(state, profile = {}) {
    state.storytellerProfile = {
      id: profile.id || "",
      name: profile.name || "空",
      avatar: profile.avatar || ""
    };
  },
  setSpectator: set("isSpectator"),
  setOwner: set("isOwner"),
  setReconnecting: set("isReconnecting"),
  setPlayerCount: set("playerCount"),
  setPing: set("ping"),
  setVotingSpeed: set("votingSpeed"),
  setVoteInProgress: set("isVoteInProgress"),
  setMarkedPlayer: set("markedPlayer"),
  setNomination: set("nomination"),
  setVoteInstance: set("voteInstance"),
  setVoteRestartContext: set("voteRestartContext"),
  setVoteHistoryAllowed: set("isVoteHistoryAllowed"),
  // Socket middleware owns both commands; these mutations are intentional
  // command hooks and do not create a second client-side authority.
  requestVoteHistory() {},
  finalizeVote() {},
  restartVote() {},
  setAnonymousVote: set("anonymousVote"),
  // 说书人切换匿名投票：由 socket 层负责本地切换 + 同步服务端。
  requestAnonymousVote() {},
  setPublicVoteless(state, payload) {
    const source = payload && typeof payload === "object" ? payload : null;
    state.publicVoteless = source
      ? {
          dayIndex: Math.max(1, Math.floor(Number(source.dayIndex)) || 1),
          values: Array.isArray(source.values) ? source.values.map(Boolean) : []
        }
      : null;
  },
  claimSeat: set("claimedSeat"),
  setConfirmedSeat: set("confirmedSeat"),
  claimStoryteller() {},
  releaseStoryteller() {},
  kickPlayer() {},
  clearAllSeats() {},
  disbandRoom() {},
  distributeRoles: set("isRolesDistributed"),
  setRolesDistributedVisual: set("isRolesDistributed"),
  // Socket middleware delivers this pending identity only to one seated user.
  sendPlayerIdentity() {},
  requestCoPlay() {},
  kickSeat() {},
  setBootlegger(state, content = "") {
    state.bootlegger = String(content || "").slice(0, 1000);
  },
  setUseOldOrder(state, settings = {}) {
    state.isUseOldOrder = { ...state.isUseOldOrder, ...settings };
  },
  setUseOldRole(state, settings = {}) {
    state.isUseOldRole = { ...state.isUseOldRole, ...settings };
  },
  setPrivateRoleType(state, { playerId, team } = {}) {
    if (!playerId) return;
    state.privateRoleTypes = { ...state.privateRoleTypes, [playerId]: team || "" };
  },
  setPrivateRoleAlignment(state, { playerId, alignment } = {}) {
    if (!playerId) return;
    state.privateRoleAlignments = {
      ...state.privateRoleAlignments,
      [playerId]: alignment === "evil" ? "evil" : alignment === "good" ? "good" : ""
    };
  },
  setBluffEligibility(state,value) { state.bluffEligibility=value; },
  setBluffDraftStatus(state,value) { state.bluffDraftStatus=value; },
  setBluffDeliveryStatus(state,value) { state.bluffDeliveryStatus=value; },
  setPrivateIdentity(state, { playerId, roleId, alignment, seatIndex } = {}) {
    if (!playerId) return;
    state.privateIdentities = {
      ...state.privateIdentities,
      [playerId]: {
        roleId: roleId || "",
        alignment: alignment === "evil" ? "evil" : "good",
        // A delivered identity belongs to the seat that received it. Keeping
        // this index prevents a player's previous private card from following
        // them for a few frames when they move to another seat.
        seatIndex: Number.isInteger(seatIndex) ? seatIndex : -1
      }
    };
  },
  setStorytellerDeliveredIdentity(state, { index, roleId, alignment, identityVersion } = {}) {
    if (!Number.isInteger(index) || index < 0) return;
    const version = Math.max(0, Number(identityVersion) || 0);
    const previous = state.storytellerDeliveredIdentities[index];
    /*
     * 已确认版本只能前进。一次「发送身份」会同时产生 identityPublish 与给
     * 玩家的 direct 身份两条服务端写入（各推进一次版本），两条回执若乱序
     * 到达，先到的较高版本会被后到的较低版本覆盖 —— 那会让客户端拿一个
     * 偏低的 baseVersion 去发下一笔，被服务器误判成 stale。
     */
    if (previous && Number(previous.identityVersion) > version) return;
    state.storytellerDeliveredIdentities = {
      ...state.storytellerDeliveredIdentities,
      [index]: {
        roleId: roleId || "",
        alignment: alignment === "evil" ? "evil" : "good",
        identityVersion: version
      }
    };
  },
  replaceStorytellerDeliveredIdentities(state, identities = {}) {
    state.storytellerDeliveredIdentities = { ...identities };
  },
  revokeStorytellerPrivateView(state) {
    state.canViewGrimoire = false;
    state.storytellerDeliveredIdentities = {};
    state.storytellerAuthoritativeIdentities = {};
    state.storytellerPendingIdentities = {};
    state.privateIdentities = {};
    state.privateRoleTypes = {};
    state.privateRoleAlignments = {};
    // 个人猜测/笔记不是说书人草稿，不能因权限降级误删。
  },
  clearOwnPrivateIdentity(state, playerId) {
    if (!playerId) return;
    ["privateIdentities", "privateRoleTypes", "privateRoleAlignments"].forEach(key => {
      const next = { ...state[key] };
      delete next[playerId];
      state[key] = next;
    });
  },
  /**
   * 说书人整表重排（随机座位）之后，本页的 Delivered / 发送中簿记也要按同一个
   * permutation 搬家：这两张表都是按座位下标存的，不搬的话红绿点与
   * Delivered tooltip 会指向错误的人（服务端档案已经搬好了，页面却还在读旧下标）。
   * order[新下标] = 旧下标；permutation 覆盖范围之外的残留键原样保留。
   */
  reindexStorytellerIdentities(state, order) {
    if (!Array.isArray(order) || !order.length) return;
    const remap = source => {
      const previous = source || {};
      const next = { ...previous };
      for (let index = 0; index < order.length; index += 1) {
        delete next[index];
      }
      order.forEach((from, index) => {
        const entry = previous[from];
        if (entry) next[index] = entry;
      });
      return next;
    };
    state.storytellerDeliveredIdentities = remap(state.storytellerDeliveredIdentities);
    state.storytellerAuthoritativeIdentities = remap(state.storytellerAuthoritativeIdentities);
    state.storytellerPendingIdentities = remap(state.storytellerPendingIdentities);
  },
  setStorytellerPendingIdentity(state, { index, operationId, roleId, alignment } = {}) {
    if (!Number.isInteger(index) || index < 0 || !operationId) return;
    state.storytellerPendingIdentities = {
      ...state.storytellerPendingIdentities,
      [index]: {
        operationId,
        roleId: roleId || "",
        alignment: alignment === "evil" ? "evil" : "good"
      }
    };
  },
  clearStorytellerPendingIdentity(state, { index, operationId } = {}) {
    if (!Number.isInteger(index) || index < 0) return;
    const current = state.storytellerPendingIdentities[index];
    if (!current) return;
    // 带 operationId 时只清同一笔；不带时（例如整桌重置）无条件清。
    if (operationId && current.operationId !== operationId) return;
    const next = { ...state.storytellerPendingIdentities };
    delete next[index];
    state.storytellerPendingIdentities = next;
  },
  clearStorytellerPendingIdentities(state) {
    state.storytellerPendingIdentities = {};
  },
  distributeRoleTypes() {},
  toggleOwnRole(state) {
    state.isOwnRoleHidden = !state.isOwnRoleHidden;
  },
  // A newly delivered identity must be immediately visible. Otherwise a
  // previously chosen local "hide identity" state makes the update appear to
  // fail until the browser is refreshed.
  revealOwnRole(state) {
    state.isOwnRoleHidden = false;
  },
  setPrivateRoleNote(state, { playerId, roleId } = {}) {
    if (!playerId) return;
    state.privateRoleNotes = {
      ...state.privateRoleNotes,
      [playerId]: roleId || ""
    };
  },
  setChatTarget: set("chatTarget"),
  setChatPolicy: set("chatPolicy"),
  setChatSendError: set("chatSendError"),
  addChatContact(state, playerId) {
    if (!playerId || state.chatContacts.includes(playerId)) return;
    state.chatContacts = [...state.chatContacts, playerId];
  },
  addChatMessage(state, message = {}) {
    if (!message.id || state.chatMessages.some(item => item.id === message.id))
      return;
    state.chatMessages = [...state.chatMessages, message].slice(-500);
    if (message.kind === "public") {
      const notice = {
        id: message.id,
        senderId: message.senderId || "",
        text: message.text || ""
      };
      if (message.system === true && message.source === "room-mini-games" && message.senderId === "__system__") {
        // 小游戏专用队列，不接管普通公聊的既有单条通知路径。
        state.miniGameChatNotices = [...state.miniGameChatNotices, { ...notice, kind: "public", system: true, source: "room-mini-games" }].slice(-200);
      } else state.publicChatNotice = notice;
      if (!state.publicMarqueeSeenIds.includes(message.id)) {
        state.publicMarqueeSeenIds = [...state.publicMarqueeSeenIds, message.id].slice(-2048);
        state.publicMarqueeEvents = [...state.publicMarqueeEvents, { ...message }].slice(-200);
      }
    }
    if (message.kind === "direct") {
      const participants = [message.senderId, ...(message.recipientIds || [])];
      const contactId = participants.includes(state.playerId)
        ? participants.find(id => id && id !== state.playerId)
        : "";
      if (contactId && !state.chatContacts.includes(contactId)) {
        state.chatContacts = [...state.chatContacts, contactId];
      }
    }
  },
  // 历史恢复只填充聊天窗口：不触发 publicChatNotice、弹幕或未读。服务端已
  // 按当前局与查看权限过滤，这里只做结构去重与会话入口恢复。
  replaceChatHistory(state, { messages } = {}) {
    const seen = new Set();
    state.chatMessages = (Array.isArray(messages) ? messages : [])
      .filter(message => message && message.id && !seen.has(message.id) && seen.add(message.id))
      .slice(-500);
    // 重连补发走chatHistory，登记身份但绝不生成弹幕事件。
    state.publicMarqueeSeenIds = [...new Set([...state.publicMarqueeSeenIds,
      ...state.chatMessages.filter(m => m.kind === "public").map(m => m.id)])].slice(-2048);
    const contacts = new Set(state.chatContacts || []);
    state.chatMessages.forEach(message => {
      if (message.kind !== "direct") return;
      const participants = [message.senderId, ...(message.recipientIds || [])];
      if (!participants.includes(state.playerId)) return;
      const peer = participants.find(id => id && id !== state.playerId);
      if (peer) contacts.add(peer);
    });
    state.chatContacts = [...contacts];
    const available = new Set(state.chatMessages.map(message => message.id));
    state.chatUnreadIds = state.chatUnreadIds.filter(id => available.has(id));
  },
  resetRoundChat(state, payload = {}) {
    state.chatReadIds = [];
    state.chatMessages = Array.isArray(payload.messages) ? payload.messages : [];
    state.chatFlights = [];
    state.publicChatNotice = null;
    state.publicMarqueeEvents = [];
    state.publicMarqueeSeenIds = [];
    state.marqueeAnnouncement = null;
    state.miniGameChatNotices = [];
    state.chatContacts = [];
    state.chatTarget = "public";
    state.chatUnreadIds = [];
    // 离开房间后回到默认值：它属于“上一个房间”，新房间以服务端下发为准。
    state.chatMode = "allow_all";
    state.chatEpoch += 1;
  },
  consumePublicMarqueeEvents(state, ids = []) {
    const consumed = new Set(ids);
    state.publicMarqueeEvents = state.publicMarqueeEvents.filter(m => !consumed.has(m.id));
  },
  markMarqueeAnnouncementPlayed(state, { key, play } = {}) {
    if (state.marqueeAnnouncement?.key === key)
      state.marqueeAnnouncement = { ...state.marqueeAnnouncement, played: Math.max(state.marqueeAnnouncement.played, play) };
  },
  setMarqueeGameScope(state, gameId) {
    state.marqueeGameScopeId = typeof gameId === "string" ? gameId : "";
    syncVictoryAnnouncement(state);
  },
  addChatFlight(state, flight = {}) {
    if (!flight.id || !flight.senderId || !Array.isArray(flight.recipientIds))
      return;
    if (state.chatFlights.some(item => item.id === flight.id)) return;
    state.chatFlights = [...state.chatFlights, flight].slice(-80);
  },
  // 未读红点只关心“有没有”，所以这里按消息 id 去重记录；socket 层已经
  // 排除了自己发的消息、连接时补发的历史消息，以及此刻正在看的消息。
  markChatUnread(state, message = {}) {
    const id = message && message.id;
    if (!id || state.chatUnreadIds.includes(id)) return;
    state.chatUnreadIds = [...state.chatUnreadIds, id].slice(-200);
  },
  // 进入聊天并真正看到这些消息（对应分页已经显示）时清掉未读。
  markChatRead(state, ids = []) {
    if (!Array.isArray(ids) || !ids.length) return;
    state.chatReadIds = [...new Set([...state.chatReadIds, ...ids])].slice(-500);
    const read = new Set(ids);
    const next = state.chatUnreadIds.filter(id => !read.has(id));
    if (next.length !== state.chatUnreadIds.length) state.chatUnreadIds = next;
  },
  setChatReadState(state, ids = []) {
    state.chatReadIds = Array.isArray(ids) ? ids.slice(-500) : [];
    const read = new Set(state.chatReadIds);
    state.chatUnreadIds = state.chatUnreadIds.filter(id => !read.has(id));
  },
  clearChatUnread(state) {
    if (state.chatUnreadIds.length) state.chatUnreadIds = [];
  },
  /**
   * 服务端权威下发的房间级白天私聊范围。只接受三档白名单值：任何异常包
   * 都不能把界面切到一个不存在的模式。
   */
  setChatMode(state, mode) {
    state.chatMode = ["allow_all", "deny_all", "neighbors_only"].includes(mode)
      ? mode
      : "allow_all";
  },
  // 说书人修改房间设置：由 socket 层发送，服务端广播回来才真正生效。
  requestChatMode() {},
  upsertChatGroup(state, group = {}) {
    if (!group.id) return;
    const next = { ...group, memberIds: [...new Set(group.memberIds || [])] };
    const index = state.chatGroups.findIndex(item => item.id === next.id);
    if (index < 0) state.chatGroups = [...state.chatGroups, next];
    else {
      state.chatGroups = [...state.chatGroups];
      state.chatGroups.splice(index, 1, next);
    }
  },
  removeChatGroup(state, payload) {
    const groupId = typeof payload === "string" ? payload : payload.groupId;
    state.chatGroups = state.chatGroups.filter(group => group.id !== groupId);
    if (payload && payload.coPlay) {
      const removedIds = new Set(state.chatMessages.filter(message => message.groupId === groupId).map(message => message.id));
      state.chatUnreadIds = state.chatUnreadIds.filter(id => !removedIds.has(id));
      // 主持仍可从独立审计页读取原群；玩家不再恢复旧群历史。
      if (state.isSpectator) state.chatMessages = state.chatMessages.filter(message => message.groupId !== groupId);
    }
    if (state.chatTarget === `group:${groupId}`) state.chatTarget = payload.fallbackTarget || "public";
  },
  // Socket middleware owns these side effects. They must still exist as Vuex
  // mutations; otherwise Vuex drops the commit before the middleware can send
  // the public/direct/group message to the room server.
  sendChat() {},
  requestChatTarget() {},
  updateChatGroup() {},
  setQuickWakeSnapshot(state, payload = {}) {
    state.quickWakeTasks = Array.isArray(payload.tasks) ? payload.tasks : [];
    state.quickWakeRoleReference = Array.isArray(payload.roleReference)
      ? payload.roleReference
      : [];
    state.quickWakeGlobalPresets = Array.isArray(payload.globalPresets) ? payload.globalPresets : [];
    state.quickWakePersonalPresets = Array.isArray(payload.personalPresets) ? payload.personalPresets : [];
  },
  openQuickWake(state, payload = {}) {
    const seatIndex = Number(payload.seatIndex);
    state.quickWakeEditor = {
      open: true,
      seatIndexes: Number.isInteger(seatIndex) && seatIndex >= 0 ? [seatIndex] : [],
      taskId: String(payload.taskId || "")
    };
  },
  closeQuickWake(state) {
    state.quickWakeEditor = { open: false, seatIndexes: [], taskId: "" };
  },
  setQuickWakeResult(state, payload) {
    state.quickWakeLastResult = payload || null;
  },
  sendQuickWake() {},
  acknowledgeQuickWake() {},
  replyQuickWake() {},
  updateQuickWakePreset() {},
  setCanViewGrimoire: set("canViewGrimoire"),
  setReceivedGrimoire(state, snapshot) {
    if (snapshot && state.receivedGrimoire?.roomId === snapshot.roomId && state.receivedGrimoire?.gameId === snapshot.gameId && state.receivedGrimoire?.seatIndex === snapshot.seatIndex && state.receivedGrimoire.sequence > snapshot.sequence) return;
    state.receivedGrimoire = snapshot || null;
    if (!snapshot) state.grimoireNotice = null;
  },
  setGrimoireNotice: set("grimoireNotice"),
  requestGrimoire() {},
  requestDiscussionTimer() {},
  requestTimerState() {},
  setPostGameReview: set("isPostGameReview"),
  setRoomClosedNotice(state, notice = {}) {
    state.roomClosedNotice = {
      reason: String(notice.reason || "stale"),
      message: String(notice.message || "该房间已失效，请重新加入或创建房间")
    };
  },
  clearRoomClosedNotice(state) {
    state.roomClosedNotice = null;
  },
  setVoiceState(state, voiceState = {}) {
    const phase = voiceState.phase || "night";
    const dayIndex = Math.max(1, Math.floor(Number(voiceState.dayIndex)) || 1);
    const game = voiceState.game || {};
    state.voiceState = {
      phase,
      dayIndex,
      // 当前这一局由服务端裁决并随 voiceState 广播；刷新、掉线重连、
      // 换手机后按钮状态都能正确恢复，客户端不自行判断。
      game: {
        id: String(game.id || ""),
        allRecall: game.allRecall === true,
        number: Math.max(0, Math.floor(Number(game.number)) || 0),
        status: ["playing", "ended", "idle"].includes(game.status)
          ? game.status
          : "idle",
        startedAt: game.startedAt || "",
        endedAt: game.endedAt || "",
        winner: game.winner === "good" || game.winner === "evil" ? game.winner : null
      },
      rooms: Array.isArray(voiceState.rooms) ? voiceState.rooms : [],
      chairEnabled: !!voiceState.chairEnabled,
      chairSpeakerIds: Array.isArray(voiceState.chairSpeakerIds)
        ? voiceState.chairSpeakerIds
        : [],
      mutedIds: Array.isArray(voiceState.mutedIds) ? voiceState.mutedIds : [],
      allMuted: !!voiceState.allMuted
    };
    // 投票组件只属于「白天提名」。阶段一旦离开提名，任何来源（含迟到的
    // 全量快照）都不允许把圆桌重新推回唱票画面。
    if (phase !== "nomination" && (state.nomination || state.isVoteInProgress)) {
      state.nomination = false;
      state.votes = [];
      state.lockedVote = 0;
      state.isVoteInProgress = false;
    }
    // 旧字段继续保留给历史代码读取，但它必须跟着权威天数走，不能再由
    // 「一次处决裁定」自行加一。
    state.dayNumber = dayIndex;
    // 记住本局的 gameId：结束那一局之后 game.id 会变空，但界面仍应继续
    // 显示刚刚结束那一局的提名记录，而不是突然清空。
    if (game.id) state.voteHistoryGameId = String(game.id);
    syncVictoryAnnouncement(state);
  },
  setVoiceToken: set("voiceToken"),
  setMiniGames(state, payload) {
    state.miniGames = payload && payload.roomId === state.sessionId ? payload : null;
  },
  setMiniGameNotice: set("miniGameNotice"),
  setDayPrivateListen(state, payload) {
    state.dayPrivateListen = payload || { available: false, enabled: false, selectedSlot: 1, maxSlot: 10, revision: 0, status: "revoked", credentials: null };
  },
  setDayPrivateListenVolume(state, value) {
    state.dayPrivateListenVolume = Math.min(100, Math.max(0, Number(value) || 0));
  },
  setPublicVoiceToken: set("publicVoiceToken"),
  /**
   * 游戏结束的全房间广播。复用现有走马灯位（publicChatNotice），
   * 用 system 标记让走马灯直接显示整句，而不拼成“说书人：xxx”。
   */
  setGameResult(state, payload = {}) {
    const winner = payload.winner === "evil" ? "evil" : payload.winner === "good" ? "good" : "";
    if (!winner) return;
    state.gameResult = {
      winner,
      number: Math.max(1, Math.floor(Number(payload.number)) || 1),
      endedAt: payload.endedAt || new Date().toISOString()
    };
    // 循环只依赖已收到的权威局状态；重复gameResult不再制造随机通知ID。
    syncVictoryAnnouncement(state);
  },
  /**
   * 新一局开始时清掉本地的私密身份缓存：玩家不该继续看到上一局的身份，
   * 授权魔典、身份正反面与伪装阵营也一并回到初始状态。
   */
  clearDeliveredIdentities(state) {
    state.marqueeAnnouncement = null;
    state.privateIdentities = {};
    state.privateRoleNotes = {};
    state.privateRoleTypes = {};
    state.privateRoleAlignments = {};
    state.storytellerDeliveredIdentities = {};
    state.storytellerAuthoritativeIdentities = {};
    state.storytellerPendingIdentities = {};
    state.canViewGrimoire = false;
    // 公开复盘同样严格属于上一局。服务端的 gameReset 会先清掉复盘快照，
    // 在线玩家也必须在同一个本地清理点撤销复盘显示；否则角色已经清空，
    // isPostGameReview 却仍为 true，会把自己的头像压成一个空白角色 Token。
    state.isPostGameReview = false;
    state.isOwnRoleHidden = false;
    state.isRolesDistributed = false;
  },
  setVoiceSpeaking(state, payload = {}) {
    state.voiceSpeaking = {
      roomId: payload.roomId || "",
      speakerIds: Array.isArray(payload.speakerIds) ? payload.speakerIds : []
    };
  },
  requestVoiceAction() {},
  requestInteraction() {},
  addInteraction(state, interaction = {}) {
    if (!interaction.id || state.interactions.some(item => item.id === interaction.id)) return;
    state.interactions = [...state.interactions, interaction].slice(-12);
  },
  removeInteraction(state, id) {
    state.interactions = state.interactions.filter(item => item.id !== id);
  },
  // The menu uses this request mutation before the actual leave mutation so
  // the websocket can explicitly release a seat while it is still connected.
  // `leaveRoom` below remains the single cleanup path for every kind of exit.
  requestLeaveRoom() {},
  cacheSeatIdentity() {},
  sendGrimoire() {},
  publishPostGameReview() {},
  distributeBluffs() {},
  distributeBluffsToSeat() {},
  setDiscussionTimer(state, timer = {}) {
    // 服务器/旧客户端可能带着 `null` 或缺失字段的计时器包进来；这里必须
    // 兜底，否则一次空快照会让整个页面抛错卡死。
    if (!timer || typeof timer !== "object") return;
    const source = timer;
    if (Number(source.revision) < Number(state.discussionTimer.revision || 0)) return;
    if (Number(source.revision) === Number(state.discussionTimer.revision || 0) && Number(source.serverNow) < Number(state.discussionTimer.serverNow || 0)) return;
    state.discussionTimer = {
      durationSeconds: Math.max(0, Number(source.durationSeconds) || 0),
      remainingSeconds: Math.max(0, Number(source.remainingSeconds) || 0),
      running: !!source.running,
      startedAt: source.startedAt || null
      ,deadlineAt: Number(source.deadlineAt) || null,
      serverNow: Number(source.serverNow) || null,
      revision: Number(source.revision) || 0,
      receivedAt: typeof performance !== "undefined" ? performance.now() : Date.now()
    };
  },
  // Leaving a room must not leave any room-specific state in the lobby.
  // The profile used by the lobby deliberately lives outside this module.
  leaveRoom(state) {
    state.receivedGrimoire = null;
    state.grimoireNotice = null;
    state.chatReadIds = [];
    state.miniGames = null;
    state.miniGameNotice = "";
    state.miniGameChatNotices = [];
    state.sessionId = "";
    state.isSpectator = false;
    state.isOwner = false;
    state.isReconnecting = false;
    state.playerCount = 0;
    state.ping = 0;
    state.claimedSeat = -1;
    state.confirmedSeat = -1;
    state.dayPrivateListen = { available: false, enabled: false, selectedSlot: 1, maxSlot: 10, revision: 0, status: "disabled", credentials: null };
    state.storytellerProfile = { id: "", name: "空", avatar: "" };
    state.nomination = false;
    state.voteInstance = 0;
    state.voteRestartContext = null;
    state.votes = [];
    state.lockedVote = 0;
    state.isVoteInProgress = false;
    state.voteHistory = [];
    state.voteHistoryGameId = "";
    state.dayNumber = 1;
    state.voiceState = {
      phase: "night",
      dayIndex: 1,
      game: { id: "", number: 0, status: "idle", allRecall: false, startedAt: "", endedAt: "", winner: null },
      rooms: [],
      chairEnabled: false,
      chairSpeakerIds: [],
      mutedIds: [],
      allMuted: false
    };
    state.dayHistory = [];
    state.pendingDayResolution = null;
    state.markedPlayer = -1;
    state.isVoteHistoryAllowed = true;
    state.isRolesDistributed = false;
    state.isOwnRoleHidden = false;
    state.bootlegger = "";
    state.isUseOldOrder = { pithag: false, professor: false };
    state.isUseOldRole = {
      balloonist: false,
      acrobat: false,
      lilmonsta: false,
      alchemist: false,
      lycanthrope: false
    };
    state.privateRoleNotes = {};
    state.privateRoleTypes = {};
    state.privateRoleAlignments = {};
    state.privateIdentities = {};
    state.storytellerDeliveredIdentities = {};
    state.storytellerAuthoritativeIdentities = {};
    state.storytellerPendingIdentities = {};
    state.chatMessages = [];
    state.chatSendError = "";
    state.chatPolicy = { publicChatMaxLength: 1000, publicMarqueeMaxLength: 50, publicMarqueeEllipsis: true };
    state.chatFlights = [];
    state.interactions = [];
    state.publicChatNotice = null;
    state.chatEpoch = 0;
    state.publicMarqueeEvents = [];
    state.publicMarqueeSeenIds = [];
    state.marqueeAnnouncement = null;
    state.chatGroups = [];
    state.marqueeGameScopeId = null;
    state.chatContacts = [];
    state.chatTarget = "public";
    state.chatUnreadIds = [];
    state.quickWakeTasks = [];
    state.quickWakeRoleReference = [];
    state.quickWakeGlobalPresets = [];
    state.quickWakePersonalPresets = [];
    state.quickWakeEditor = { open: false, seatIndexes: [], taskId: "" };
    state.quickWakeLastResult = null;
    state.canViewGrimoire = false;
    state.isPostGameReview = false;
    state.gameResult = null;
    state.discussionTimer = {
      durationSeconds: 0,
      remainingSeconds: 0,
      running: false,
      startedAt: null
    };
  },
  setSessionId(state, sessionId) {
    state.sessionId = sessionId
      .toLocaleLowerCase()
      .replace(/[^0-9a-z]/g, "")
      .substr(0, 10);
  },
  nomination(
    state,
    { nomination, voteInstance, votes, votingSpeed, lockedVote, isVoteInProgress } = {}
  ) {
    state.nomination = nomination || false;
    if (Number.isInteger(Number(voteInstance)) && Number(voteInstance) >= 0) {
      state.voteInstance = Number(voteInstance);
    }
    state.votes = votes || [];
    state.votingSpeed = votingSpeed || state.votingSpeed;
    state.lockedVote = lockedVote || 0;
    state.isVoteInProgress = isVoteInProgress || false;
  },
  /**
   * Create an entry in the vote history log. Requires current player array because it might change later in the game.
   * Only stores votes that were completed.
   * @param state
   * @param players
   */
  addHistory(state, players) {
    if (!state.isVoteHistoryAllowed && state.isSpectator) return;
    if (!state.nomination || state.lockedVote <= players.length) return;
    const isExile = players[state.nomination[1]].role.team === "traveler";
    state.voteHistory.push({
      timestamp: new Date(),
      // 每条记录都属于某一局。界面按它过滤，避免新一局的天数重新从
      // 第 1 天开始后，新旧两局的“第 1 天”记录混在一起。
      gameId: state.voteHistoryGameId || (state.voiceState.game && state.voiceState.game.id) || "",
      gameNumber: (state.voiceState.game && state.voiceState.game.number) || 0,
      // 第几个白天发生的这次投票。天数由阶段机（黑夜→白天→提名→黑夜）
      // 推进，和唱票弹窗里显示的编号完全一致。
      day: Math.max(1, Math.floor(Number(state.dayNumber)) || 1),
      // 记下本次唱票是否处于匿名模式：普通玩家查看投票记录时不显示真实
      // 票数与投票人名单。
      anonymous: state.anonymousVote === true,
      nominator: players[state.nomination[0]].name,
      nominee: players[state.nomination[1]].name,
      type: isExile ? "Exile" : "Execution",
      majority: Math.ceil(
        players.filter(player => !player.isDead || isExile).length / 2
      ),
      votes: players
        .filter((player, index) => state.votes[index])
        .map(({ name }) => name)
    });
  },
  clearVoteHistory(state) {
    // 只清当前这一局，旧局记录保留在存档里（与原按钮“清除投票记录”的
    // 可见范围一致：界面上本来就只显示当前这一局）。
    state.voteHistory = state.voteHistory.filter(
      vote => (vote.gameId || "") !== (state.voteHistoryGameId || "")
    );
  },
  setVoteHistory(state, payload = {}) {
    const gameId = String(payload.gameId || state.voteHistoryGameId || "");
    const records = Array.isArray(payload.records) ? payload.records : [];
    state.voteHistoryGameId = gameId;
    state.voteHistory = records.map(record => ({
      ...record,
      gameId: String(record.gameId || gameId),
      timestamp: record.timestamp instanceof Date
        ? record.timestamp
        : new Date(record.timestamp)
    }));
  },
  setDayState(state, { dayHistory, pendingDayResolution } = {}) {
    // dayNumber 现在由服务器阶段机（voiceState.dayIndex）统一广播，旧的
    // 快照字段不再覆盖它；这里只同步当日记录与待裁定结果。
    state.dayHistory = dayHistory || state.dayHistory;
    state.pendingDayResolution = pendingDayResolution || null;
  },
  setPendingDayResolution: set("pendingDayResolution"),
  clearPendingDayResolution(state) {
    state.pendingDayResolution = null;
  },
  // “待处决”的骷髅头来自 markedPlayer。清空座位时必须同时清掉它，
  // 否则开新一局时上一局的骷髅会一直挂在座位上。
  clearMarkedPlayer(state) {
    state.markedPlayer = -1;
  },
  resolveDay(state, outcome) {
    if (state.pendingDayResolution) {
      state.dayHistory.push({
        ...state.pendingDayResolution,
        outcome,
        resolvedAt: new Date().toISOString()
      });
    }
    state.pendingDayResolution = null;
    // 天数由阶段机推进（提名 → 黑夜 时 +1），裁定本身不再改天数，
    // 否则同一天里每一次唱票都会把日期往前推一格。
  },
  /**
   * Store a vote with and without syncing it to the live session.
   * This is necessary in order to prevent infinite voting loops.
   * @param state
   * @param vote
   */
  vote: handleVote,
  voteSync: handleVote,
  lockVote(state, lock) {
    state.lockedVote = lock !== undefined ? lock : state.lockedVote + 1;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
